/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import { RootState, AppDispatch } from "app/store";
import { uploadImageAsset, uploadImageFile } from "app/api";
import I18n from "common/i18n";
import Constants from "common/constant";
import { useAssetCache } from "common/hook";
import { ComponentTypeIds, ComponentCategory } from "common/components";
import { actions } from "features/creator/slice";
import { useActionCommand } from "features/creator/utils";
import { DrawingModel, GroupingDefault, ActionCommandType } from "features/creator/types";

const dataUrlToBlob = async (dataUrl: string) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const hasCanvasContext = (canvas: fabric.Canvas) => {
  const contextContainer = (
    canvas as unknown as { contextContainer?: CanvasRenderingContext2D | null }
  ).contextContainer;
  return Boolean(contextContainer);
};

export const FreeDrawingPanel = ({
  screenId,
  width,
  height,
}: {
  screenId: string;
  width: number;
  height: number;
}) => {
  const handleAction = useActionCommand();
  const dispatch = useDispatch<AppDispatch>();
  const { onAssetSaved } = useAssetCache();
  const ref = useRef<HTMLCanvasElement>(null);
  const active = useSelector((state: RootState) => state.user.active);
  const [fabricCanvas, setFabricCanvas] = useState<fabric.Canvas>(null);
  const drawing = useSelector((state: RootState) => state.creator.drawing);
  const [show, setShow] = useState(drawing.enable);
  const drawingModelRef = useRef(drawing.model);
  const setDrawingModelRef = (mode: DrawingModel) => {
    drawingModelRef.current = mode;
  };

  useEffect(() => {
    if (drawing.enable) {
      setShow(true);
      if (ref.current && !fabricCanvas) {
        dispatch(
          actions.updateSelectedComponentId({
            screenId: screenId,
            id: screenId,
          }),
        );
        dispatch(actions.updateGrouping(GroupingDefault));

        //@ts-ignore
        const canvas = new fabric.Canvas(ref.current, { isDrawingMode: true });
        canvas.freeDrawingBrush.width = drawing.pen.radius;
        canvas.freeDrawingBrush.color = drawing.color;
        setFabricCanvas(canvas);
      }
    } else {
      if (fabricCanvas) {
        const objects = fabricCanvas.toObject().objects;
        const cleanupCanvas = () => {
          if (hasCanvasContext(fabricCanvas)) {
            fabricCanvas.clear();
            fabricCanvas.setWidth(0);
            fabricCanvas.setHeight(0);
            fabricCanvas.dispose();
          }
          setFabricCanvas(null);
          if (ref.current) {
            ref.current.width = 0;
            ref.current.height = 0;
            ref.current = null;
          }
          setShow(false);
        };

        if (objects.length > 0) {
          // create new drawing component
          const lefts = fabricCanvas.toObject().objects.map((o) => o.left);
          const tops = fabricCanvas.toObject().objects.map((o) => o.top);
          const rights = fabricCanvas
            .toObject()
            .objects.map((o) => o.left + o.width + drawing.pen.radius);
          const bottoms = fabricCanvas
            .toObject()
            .objects.map((o) => o.top + o.height + drawing.pen.radius);

          const minX = Math.min(...lefts);
          const minY = Math.min(...tops);
          const maxX = Math.max(...rights);
          const maxY = Math.max(...bottoms);
          const width = maxX - minX;
          const height = maxY - minY;
          const exportWidth = Math.max(1, Math.ceil(width));
          const exportHeight = Math.max(1, Math.ceil(height));

          const svg = fabricCanvas.toSVG({
            viewBox: {
              x: minX,
              y: minY,
              width: maxX - minX,
              height: maxY - minY,
            },
            width: "100%",
            height: "100%",
          });

          const uuid = uuidv4();
          const fileBlob = new Blob([svg], {
            type: "image/svg+xml;charset=utf-8",
          });
          const filename = `${I18n.t(
            "MSG_CREATOR_COMPONENT_NAME_DRAWING",
          )}_${dayjs().format("YYYY_MM_DD_HH_mm_ss")}`;
          const svgObjectKey = `users/${active.uid}/assets/drawing/${uuid}.svg`;
          const pngObjectKey = `users/${active.uid}/assets/drawing/${uuid}.png`;
          const pngUrl = `${Constants.assetHost}/${pngObjectKey}`;

          const saveAssets = async () => {
            let resolvedPngUrl: string | undefined;
            try {
              const pngDataUrl = fabricCanvas.toDataURL({
                format: "png",
                left: minX,
                top: minY,
                width: exportWidth,
                height: exportHeight,
              });
              const pngBlob = await dataUrlToBlob(pngDataUrl);
              await uploadImageFile({
                is_sync: false,
                fileBlob: pngBlob,
                filename: pngObjectKey,
                metadata: {
                  width: String(exportWidth),
                  height: String(exportHeight),
                },
                contentType: "image/png",
              });
              resolvedPngUrl = pngUrl;
            } catch (error) {
              console.error("Failed to export drawing png:", error);
            }

            const response = await uploadImageAsset({
              uid: active.uid,
              is_sync: false,
              fileBlob,
              object_key: svgObjectKey,
              filename,
              tags: I18n.t("MSG_CREATOR_COMPONENT_NAME_DRAWING"),
              metadata: {
                width: String(width),
                height: String(height),
                ...(resolvedPngUrl ? { png_url: resolvedPngUrl } : {}),
              },
              contentType: "image/svg+xml",
            });
            onAssetSaved(active.uid, response.data);
          };

          saveAssets()
            .then(() => {
              cleanupCanvas();
            })
            .catch((error) => {
              console.error("Failed to save drawing assets:", error);
              cleanupCanvas();
            });
          handleAction({
            type: ActionCommandType.ADD_COMPONENT,
            componentType: ComponentTypeIds.DRAWING,
            category: ComponentCategory.ACTION,
            screenId: screenId,
            position: { x: minX, y: minY },
            drawing: {
              svg,
              width,
              height,
            },
          });
        }
        if (objects.length === 0) {
          cleanupCanvas();
        }
      }
    }
  }, [drawing.enable, ref.current]);

  useEffect(() => {
    if (fabricCanvas) {
      setDrawingModelRef(drawing.model);
      if (drawing.model === DrawingModel.PEN) {
        //@ts-ignore
        fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(fabricCanvas);
        fabricCanvas.freeDrawingBrush.width = drawing.pen.radius;
        fabricCanvas.freeDrawingBrush.color = drawing.color;
      } else if (drawing.model === DrawingModel.ERASER) {
        //@ts-ignore
        fabricCanvas.freeDrawingBrush = new fabric.EraserBrush(fabricCanvas);
        fabricCanvas.freeDrawingBrush.width = drawing.eraser.radius;
      }
    }
  }, [drawing.model]);

  useEffect(() => {
    if (fabricCanvas) {
      if (drawing.model === DrawingModel.PEN) {
        fabricCanvas.freeDrawingBrush.width = drawing.pen.radius;
        fabricCanvas.freeDrawingBrush.color = drawing.color;
      } else if (drawing.model === DrawingModel.ERASER) {
        fabricCanvas.freeDrawingBrush.width = drawing.eraser.radius;
      }
    }
  }, [drawing.color, drawing.pen.radius, drawing.eraser.radius]);

  useEffect(() => {
    if (fabricCanvas && hasCanvasContext(fabricCanvas)) {
      fabricCanvas.clear();
    }
  }, [drawing.reset]);

  return drawing.enable || show ? (
    <div
      id={`drawing-panel-${screenId}`}
      className="w-full h-full flex-row-view !absolute top-0 left-0 z-50"
    >
      <div
        className="w-full h-full flex-row-view !absolute top-0 left-0"
        style={{
          backgroundImage:
            "linear-gradient(0deg, transparent 31px, #d9effa 32px), linear-gradient(90deg,  transparent 31px, #d9effa 32px)",
          backgroundSize: "32px 32px",
        }}
      />
      <canvas id="drawing-canvas" ref={ref} width={width} height={height} />
    </div>
  ) : (
    <></>
  );
};
